import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowDownTrayIcon,
  EyeSlashIcon,
  MapIcon,
} from "@heroicons/react/24/outline";
import {
  geneHubClient,
  type AssociationResult,
} from "../../services/geneHubClient";
import { TargetXHeader } from "../../components/TargetXHeader";
import { PathwayExplorer } from "./PathwayExplorer";

export default function GeneHubFixed() {
  // State management
  const [selectedTA, setSelectedTA] = useState<string>("");
  const [selectedDisease, setSelectedDisease] = useState<string>("");
  const [customDisease, setCustomDisease] = useState<string>("");
  const [selectedGene, setSelectedGene] = useState<string>("");
  const [, setShowQuickTargetPanel] = useState(false);
  const [, setShowExpressionPanel] = useState(false);
  const [analysisResult, setAnalysisResult] =
    useState<AssociationResult | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("pathway-explorer");

  const { data: diseases = [], isLoading: loadingDiseases } = useQuery({
    queryKey: ["diseases", selectedTA],
    queryFn: () => geneHubClient.getDiseasesByTA(selectedTA),
    enabled: !!selectedTA,
  });

  const { data: genes = [], isLoading: loadingGenes } = useQuery({
    queryKey: ["genes", selectedTA],
    queryFn: () => geneHubClient.getGenesByTA(selectedTA),
    enabled: !!selectedTA,
  });

  // Analysis mutation
  const analysisMutation = useMutation({
    mutationFn: (params: {
      gene_id: string;
      disease_id?: string;
      custom_disease?: string;
      therapeutic_area: string;
    }) => geneHubClient.analyzeAssociation(params),
    onSuccess: (result) => {
      setAnalysisResult(result);
      setShowQuickTargetPanel(true);
      setShowExpressionPanel(true);
    },
    onError: (error) => {
      console.error("Analysis failed:", error);
      alert(`Analysis failed: ${error.message}`);
    },
  });

  const handleRunAnalysis = () => {
    if (
      !selectedTA ||
      !selectedGene ||
      (!selectedDisease && !customDisease.trim())
    ) {
      return;
    }

    const params = {
      gene_id: selectedGene,
      therapeutic_area: selectedTA,
      ...(selectedDisease
        ? { disease_id: selectedDisease }
        : { custom_disease: customDisease.trim() }),
    };

    analysisMutation.mutate(params);
  };

  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string>("");

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setSearchError("");
      // Search without requiring therapeutic area - more flexible search
      const results = await geneHubClient.searchGenes(query, undefined, 20);
      setSearchResults(results.genes || []);
    } catch (error) {
      console.error("Search error:", error);
      setSearchError("Failed to search genes. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const canRunAnalysis =
    selectedTA && selectedGene && (selectedDisease || customDisease.trim());

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-100 dark:from-slate-900 dark:via-slate-800 dark:to-purple-950 flex flex-col min-h-screen">
      {/* TargetX Header */}
      <TargetXHeader
        title="Gene Hub"
        subtitle="Curate and analyze gene lists with disease associations and therapeutic insights"
        searchPlaceholder="Search genes, diseases, therapeutic areas..."
        onSearch={handleSearch}
      />

      {/* Search Results Section */}
      {searchResults.length > 0 && (
        <div className="px-8 py-4 bg-white dark:bg-slate-800 shadow-sm border-b border-gray-200 dark:border-slate-700">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Search Results
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {searchResults.slice(0, 6).map((gene: any) => (
                <div
                  key={gene.id}
                  className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                  onClick={() => {
                    setSelectedGene(gene.id.toString());
                    if (
                      gene.therapeutic_areas &&
                      gene.therapeutic_areas.length > 0
                    ) {
                      setSelectedTA(gene.therapeutic_areas[0]);
                    }
                    setSearchResults([]);
                  }}
                >
                  <div className="font-semibold text-blue-900 dark:text-blue-200">
                    {gene.symbol}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{gene.name}</div>
                  {gene.therapeutic_areas && (
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {gene.therapeutic_areas.join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {searchResults.length > 6 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Showing first 6 of {searchResults.length} results
              </div>
            )}
          </div>
        </div>
      )}

      {isSearching && (
        <div className="px-8 py-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 dark:border-blue-400"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Searching genes...</span>
          </div>
        </div>
      )}

      {searchError && (
        <div className="px-8 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <div className="text-sm text-red-600 dark:text-red-400">{searchError}</div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 px-8 py-6 space-x-6">
        {/* Left Sidebar - Gene Browser */}
        {sidebarVisible && (
          <div className="w-80 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 flex flex-col relative">
            <button
              onClick={() => setSidebarVisible(false)}
              className="absolute right-3 top-3 z-10 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-full p-1.5 transition-colors"
              title="Hide Gene Browser"
            >
              <EyeSlashIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>

            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Gene Browser
                </h2>
                <button className="flex items-center space-x-1.5 px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-md">
                  <span>+ Add New</span>
                </button>
              </div>
            </div>

            <div className="flex-1 p-6 space-y-6 overflow-y-auto custom-scrollbar">
              {/* Gene Selection Guidance */}
              {!selectedTA && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-500 dark:bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                        Quick Start Guide
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        You can search for genes using the search bar above, or
                        follow these steps:
                        <br />
                        1. Select a therapeutic area
                        <br />
                        2. Choose a disease or enter a custom one
                        <br />
                        3. Pick a gene for analysis
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Disease Selection */}
              {selectedTA && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    Disease Selection
                  </h3>
                  {loadingDiseases ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-xl"></div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <select
                        value={selectedDisease}
                        onChange={(e) => setSelectedDisease(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 bg-gray-50 dark:bg-slate-700 hover:bg-white dark:hover:bg-slate-600 transition-all duration-200 text-sm text-gray-900 dark:text-white"
                      >
                        <option value="">Select a disease...</option>
                        {diseases.map((disease: any) => (
                          <option
                            key={disease.id}
                            value={disease.id.toString()}
                          >
                            {disease.name}
                          </option>
                        ))}
                      </select>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300 dark:border-slate-600" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-white dark:bg-slate-800 px-3 py-1 text-gray-500 dark:text-gray-400 font-medium rounded-full">
                            OR
                          </span>
                        </div>
                      </div>

                      <input
                        type="text"
                        value={customDisease}
                        onChange={(e) => setCustomDisease(e.target.value)}
                        placeholder="Enter custom disease name..."
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 bg-gray-50 dark:bg-slate-700 hover:bg-white dark:hover:bg-slate-600 transition-all duration-200 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Gene Selection */}
              {selectedTA && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    Gene Selection
                  </h3>
                  {loadingGenes ? (
                    <div className="animate-pulse">
                      <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-xl"></div>
                    </div>
                  ) : genes.length === 0 ? (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-yellow-500 dark:bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs font-bold">
                            ⚠
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-1">
                            No genes found
                          </p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            No genes available for this therapeutic area. Try
                            using the search bar above to find specific genes.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <select
                      value={selectedGene}
                      onChange={(e) => setSelectedGene(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 bg-gray-50 dark:bg-slate-700 hover:bg-white dark:hover:bg-slate-600 transition-all duration-200 text-sm text-gray-900 dark:text-white"
                    >
                      <option value="">Select a gene...</option>
                      {genes.map((gene: any) => (
                        <option key={gene.id} value={gene.id.toString()}>
                          {gene.symbol} - {gene.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Run Analysis Button */}
              <div className="pt-4">
                <button
                  onClick={handleRunAnalysis}
                  disabled={!canRunAnalysis || analysisMutation.isPending}
                  className={`w-full flex items-center justify-center px-6 py-4 font-medium rounded-xl transition-all duration-200 transform ${canRunAnalysis && !analysisMutation.isPending
                    ? "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl hover:scale-105"
                    : "bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    }`}
                >
                  {analysisMutation.isPending ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-current"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Analyzing...
                    </>
                  ) : canRunAnalysis ? (
                    "Run Analysis"
                  ) : (
                    "Complete Required Fields Above"
                  )}
                </button>

                {analysisResult && (
                  <button className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-md mt-3">
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                    Download Report
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Center Content Area */}
        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 flex flex-col">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-slate-700">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("pathway-explorer")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${activeTab === "pathway-explorer"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-slate-600"
                  }`}
              >
                <MapIcon className="h-5 w-5 mr-2" />
                Pathway Explorer
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-1">
            <PathwayExplorer />
          </div>
        </div>

        {/* Right Panel Toggle Button */}

        {/* Right Analysis Panel */}
      </div>
    </div>
  );
}
